import {
  DEVICE_OUTPUT_OPERATION_MODES,
  type DeviceInputOperationMode,
  type DeviceOutputOperationMode,
  type EquipmentType,
} from "@irriga-mais/common";
import { useAtomValue } from "jotai";
import { use, useCallback, useEffect, useMemo } from "react";
import type { AUTDevice } from "@/api/queries/account";
import { virtualDeviceConfigurationAtom } from "@/store/data";
import type { VirtualDeviceConfigurationWithModel } from "@/store/data/property-domain";
import { filterAvailableOutputs, filterDeviceAvailableInputs } from "@/utils/device";
import { getDeviceModelCommercialName } from "@/utils/device-model";

const OUTPUT_MODE_LABELS: Record<DeviceOutputOperationMode, string> = {
  continuous: "Contínuo",
  pulse: "Pulso",
  analog: "Analógico",
  meta: "Especial",
};

export interface ControllerConfigValue {
  controllerId: string | null;
  turnOnOutputIndex: number | null;
  turnOffOutputIndex: number | null;
  monitoringInputIndex: number | null;
  outputMode: DeviceOutputOperationMode;
  inputMode: DeviceInputOperationMode;
}

export interface ControllerConfigurationPanelProps {
  equipmentType: EquipmentType;
  currentConfig?: VirtualDeviceConfigurationWithModel | null;
  value: ControllerConfigValue;
  onChange: (value: ControllerConfigValue) => void;
  availableControllers: AUTDevice[];
  disabled?: boolean;
  showTurnOffOutput?: boolean;
  showMonitoringInput?: boolean;
  showModeSelection?: boolean;
  forceContinuousReason?: string | null;
  additionalModeConstraints?: (controller: AUTDevice, outputIndex: number | null) => string | null;
}

export function ControllerConfigurationPanel({
  equipmentType,
  currentConfig,
  value,
  onChange,
  availableControllers,
  disabled = false,
  showTurnOffOutput,
  showMonitoringInput = true,
  showModeSelection = true,
  forceContinuousReason,
  additionalModeConstraints,
}: ControllerConfigurationPanelProps) {
  const virtualDeviceConfigurations = useAtomValue(virtualDeviceConfigurationAtom);

  // Determine if turn off output should be shown
  const shouldShowTurnOffOutput = useMemo(() => {
    return showTurnOffOutput ?? value.outputMode === "pulse";
  }, [showTurnOffOutput, value.outputMode]);

  // Available outputs for each controller
  const availableOutputs = useMemo(() => {
    const availableEquipmentOutputs = filterAvailableOutputs(
      availableControllers,
      virtualDeviceConfigurations,
      equipmentType
    );

    // Include the current controller/output if it is already assigned to this equipment
    if (currentConfig) {
      const currentController = currentConfig.device;
      const currentTurnOnOutput = currentConfig.turn_on_physical_output_index;
      const currentOutputAvailable = availableEquipmentOutputs.find((ao) => ao.device.id === currentController.id);

      if (currentOutputAvailable && !currentOutputAvailable.outputs.includes(currentTurnOnOutput)) {
        currentOutputAvailable.outputs.push(currentTurnOnOutput);
        currentOutputAvailable.outputs.sort();
      }

      const currentTurnOffOutput = currentConfig.turn_off_physical_output_index;
      if (currentOutputAvailable &&  currentTurnOffOutput != null && !currentOutputAvailable.outputs.includes(currentTurnOffOutput)) {
        currentOutputAvailable.outputs.push(currentTurnOffOutput);
        currentOutputAvailable.outputs.sort();
      }
    }

    return availableEquipmentOutputs;
  }, [availableControllers, virtualDeviceConfigurations, equipmentType, currentConfig]);

  const availableControllersWithOutputs = useMemo(() => {
    return availableControllers.filter((controller) =>
      availableOutputs.some((ao) => ao.device.id === controller.id && ao.outputs.length > 0)
    );
  }, [availableControllers, availableOutputs]);

  // Currently selected controller
  const selectedController = useMemo(() => {
    return availableControllers.find((d) => d.id === value.controllerId) || null;
  }, [availableControllers, value.controllerId]);

  // Currently selected physical output model
  const selectedPhysicalOutput = useMemo(() => {
    if (!selectedController || !value.turnOnOutputIndex) return null;

    const controllerWithAvailableOutputs = availableOutputs.find((ao) => ao.device.id === selectedController.id);
    if (!controllerWithAvailableOutputs) return null;

    if (controllerWithAvailableOutputs.outputs.includes(value.turnOnOutputIndex)) {
      return controllerWithAvailableOutputs.device.model.outputs[value.turnOnOutputIndex - 1];
    }

    return null;
  }, [selectedController, availableOutputs, value.turnOnOutputIndex]);

  // Available inputs for the selected controller
  const availableInputs = useMemo(() => {
    if (!selectedController) return [];

    const availableInputsList = filterDeviceAvailableInputs(
      selectedController,
      virtualDeviceConfigurations,
      equipmentType
    );

    // Include the current input if it's already assigned
    if (currentConfig && currentConfig.monitoring_physical_input_index) {
      const currentInput = currentConfig.monitoring_physical_input_index;
      if (!availableInputsList.includes(currentInput)) {
        availableInputsList.push(currentInput);
        availableInputsList.sort();
      }
    }

    return availableInputsList;
  }, [selectedController, virtualDeviceConfigurations, equipmentType, currentConfig]);

  // Determine if mode is forced to continuous
  const isModeForcedContinuous = useMemo(() => {
    if (!selectedController || !value.turnOnOutputIndex) return false;

    // Check if output is exclusively continuous
    const isOutputExclusiveContinuous = selectedPhysicalOutput?.operationModes?.every((mode) => mode === "continuous");
    if (isOutputExclusiveContinuous) return true;

    // Check for additional constraints from parent
    if (additionalModeConstraints) {
      const reason = additionalModeConstraints(selectedController, value.turnOnOutputIndex);
      if (reason) return true;
    }

    return false;
  }, [selectedController, value.turnOnOutputIndex, selectedPhysicalOutput, additionalModeConstraints]);

  // Get the reason for forced continuous mode
  const forcedContinuousModeReason = useMemo(() => {
    if (!isModeForcedContinuous || !selectedController) return null;

    // Use provided reason first
    if (forceContinuousReason) return forceContinuousReason;

    // Check for additional constraints
    if (additionalModeConstraints) {
      const reason = additionalModeConstraints(selectedController, value.turnOnOutputIndex);
      if (reason) return reason;
    }

    // Default reason based on output capabilities
    const isOutputExclusiveContinuous = selectedPhysicalOutput?.operationModes?.every((mode) => mode === "continuous");
    if (isOutputExclusiveContinuous) {
      return `Saída ${value.turnOnOutputIndex} do modelo ${getDeviceModelCommercialName(selectedController.model)} exige modo CONTÍNUO.`;
    }

    return "O modo está sendo forçado para CONTINUOUS pelas regras do controlador.";
  }, [
    isModeForcedContinuous,
    selectedController,
    forceContinuousReason,
    additionalModeConstraints,
    value.turnOnOutputIndex,
    selectedPhysicalOutput,
  ]);

  // Check if analog mode is allowed
  const allowAnalogMode = useMemo(() => {
    return selectedPhysicalOutput?.operationModes?.includes("analog") ?? false;
  }, [selectedPhysicalOutput]);

  // Available operation modes
  const availableOperationModes = useMemo(() => {
    // const modes = DEVICE_OUTPUT_OPERATION_MODES.filter((mode) => {
    //   if (mode === "analog") return allowAnalogMode;
    //   return true;
    // });
    return selectedPhysicalOutput?.operationModes ?? [];
  }, [selectedPhysicalOutput?.operationModes]);

  // Determine if mode selection should be disabled (no turn on output selected or forced continuous)
  const isModeSelectionDisabled = useMemo(() => {
    return !value.turnOnOutputIndex || isModeForcedContinuous || !selectedPhysicalOutput;
  }, [value.turnOnOutputIndex, isModeForcedContinuous, selectedPhysicalOutput]);

  // Auto-correct mode when forced to continuous or when no turn on output is selected
  useEffect(() => {
    if (isModeSelectionDisabled && value.outputMode !== "continuous") {
      onChange({
        ...value,
        outputMode: "continuous",
        turnOffOutputIndex: null,
      });
    }
  }, [isModeSelectionDisabled, value, onChange]);

  // Handle controller change
  const handleControllerChange = (controllerId: string) => {
    onChange({
      ...value,
      controllerId: controllerId || null,
      turnOnOutputIndex: null,
      turnOffOutputIndex: null,
      monitoringInputIndex: null,
    });
  };

  // Handle turn on output change
  const handleTurnOnOutputChange = (outputIndex: number | null) => {
    onChange({
      ...value,
      turnOnOutputIndex: outputIndex,
      turnOffOutputIndex: value.outputMode === "pulse" ? value.turnOffOutputIndex : null,
    });
  };

  // Handle turn off output change
  const handleTurnOffOutputChange = (outputIndex: number | null) => {
    onChange({
      ...value,
      turnOffOutputIndex: outputIndex,
    });
  };

  // Handle monitoring input change
  const handleMonitoringInputChange = (inputIndex: number | null) => {
    onChange({
      ...value,
      monitoringInputIndex: inputIndex,
    });
  };

  // Handle mode change
  const handleModeChange = useCallback(
    (mode: DeviceOutputOperationMode) => {
      const newDeviceState = {
        ...value,
        outputMode: mode,
        turnOffOutputIndex: mode === "pulse" ? value.turnOffOutputIndex : null,
      };
      console.log("New device state:", newDeviceState);
      onChange(newDeviceState);
    },
    [onChange, value]
  );

  useEffect(() => {
    if (selectedPhysicalOutput && availableOperationModes.length === 1) {
      handleModeChange(availableOperationModes[0]);
    }
  }, [selectedPhysicalOutput, availableOperationModes]);

  return (
    <div className="space-y-6">
      {/* Controller Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Controlador</label>
        <select
          value={value.controllerId ?? ""}
          onChange={(e) => handleControllerChange(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none"
          disabled={disabled}
        >
          <option value="">Selecione o controlador</option>
          {availableControllersWithOutputs.map((controller) => (
            <option key={controller.id} value={controller.id}>
              {controller.metadata?.label
                ? `${controller.metadata.label} - ${controller.identifier}`
                : controller.identifier}{" "}
              - {getDeviceModelCommercialName(controller.model)}
            </option>
          ))}
        </select>
      </div>

      {/* Output Selection */}
      <div>
        <div className="flex gap-4">
          {/* Turn On Output */}
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">Saída de Ativação</label>
            <select
              value={value.turnOnOutputIndex ?? ""}
              onChange={(e) => handleTurnOnOutputChange(e.target.value ? Number(e.target.value) : null)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none"
              disabled={!value.controllerId || disabled}
            >
              <option value="">Selecione a saída de ativação</option>
              {availableOutputs
                .find((ao) => ao.device.id === value.controllerId)
                ?.outputs.map((output) => (
                  <option key={output} value={output}>
                    {output}
                  </option>
                ))}
            </select>
          </div>

          {/* Turn Off Output */}
          {shouldShowTurnOffOutput && (
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">Saída de Desativação</label>
              <select
                value={value.turnOffOutputIndex ?? ""}
                onChange={(e) => handleTurnOffOutputChange(e.target.value ? Number(e.target.value) : null)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none"
                disabled={!value.controllerId || value.outputMode !== "pulse" || disabled}
              >
                <option value="">Selecione a saída de desativação</option>
                {availableOutputs
                  .find((ao) => ao.device.id === value.controllerId)
                  ?.outputs.map((output) => (
                    <option key={output} value={output}>
                      {output}
                    </option>
                  ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Output Mode Selection */}
      {showModeSelection && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Modo</label>
          <div className="flex gap-4">
            {availableOperationModes.map((modeValue) => (
              <label key={modeValue} className="flex items-center gap-1">
                <input
                  type="radio"
                  name={`${equipmentType}-mode`}
                  value={modeValue}
                  checked={value.outputMode === modeValue}
                  onChange={() => handleModeChange(modeValue)}
                  className="text-green-600 focus:ring-green-500"
                  disabled={isModeSelectionDisabled || disabled}
                />
                <span className="text-sm">{OUTPUT_MODE_LABELS[modeValue]}</span>
              </label>
            ))}
          </div>
          <p className="mt-1 text-xs text-gray-500">Modo de operação do equipamento</p>
          {isModeForcedContinuous && forcedContinuousModeReason && (
            <p className="mt-1 text-xs text-gray-500">{forcedContinuousModeReason}</p>
          )}
        </div>
      )}

      {/* Monitoring Input Selection */}
      {showMonitoringInput && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Entrada de Monitoramento</label>
          <select
            value={value.monitoringInputIndex ?? ""}
            onChange={(e) => handleMonitoringInputChange(e.target.value ? Number(e.target.value) : null)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none"
            disabled={!value.controllerId || disabled}
          >
            <option value="">Selecione a entrada de monitoramento</option>
            {availableInputs.map((input) => (
              <option key={input} value={input}>
                {input}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}
